import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { PaymentOption } from "~/api/models/PaymentOption";
import { useFetchPaymentOptions } from "~/api/salesForce/useFetchPaymentOptions";
import Tag from "~/components/atoms/Tag/Tag";
import PricingModal from "~/components/molecules/PricingModal/PricingModal";
import PricingModalOption from "~/components/molecules/PricingModalOption/PricingModalOption";
import { useCheckoutStore } from "~/stores/checkout/useCheckoutStore";

type PaymentOptionModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

const PaymentOptionModal = ({ open, setOpen }: PaymentOptionModalProps) => {
  const intl = useIntl();
  const {
    data: paymentOptions,
    isLoading,
    isError,
    refetch,
  } = useFetchPaymentOptions();
  const [setSelectedPaymentOption] = useCheckoutStore((state) => [
    state.setSelectedPaymentOption,
  ]);

  const [selected, setSelected] = useState<string | undefined>(undefined);

  const selectOption = (option: PaymentOption) => {
    setSelected(option.apiName);
  };

  const saveOption = () => {
    if (!selected) return;
    const option = paymentOptions?.find((po) => po.apiName === selected);
    if (option) {
      setSelectedPaymentOption(option);
      setOpen(false);
    }
  };

  useEffect(() => {
    /*
    when the modal opens it assigns the previously saved paymentMethod or the first one in the list
    and refetches the payment options
    */
    if (open) {
      refetch();
    }
  }, [open]);

  return (
    <PricingModal
      open={open}
      setOpen={setOpen}
      title={intl.formatMessage({ id: "pricing.modal.paymentMethods.title" })}
      buttonText={intl.formatMessage({ id: "actions.save" })}
      buttonAction={saveOption}
    >
      <div className="grid gap-4">
        {!isLoading &&
          !isError &&
          paymentOptions?.map((option) => {
            const feeTagVariant = option.isExternal ? "neutral" : "positive";

            const feeTagText = option.isExternal
              ? intl.formatMessage({
                  id: "pricing.paymentMethod.externalProvider",
                })
              : intl.formatMessage({
                  id: "pricing.paymentMethod.noFees",
                });

            return (
              <PricingModalOption
                key={option.apiName}
                variant="2rows"
                title={option.name}
                selected={option.apiName === selected}
                onSelected={() => {
                  selectOption(option);
                }}
                priceAmount={option.totalAmount}
                customPricingTextString={option.description}
                badge={<Tag variant={feeTagVariant} copy={feeTagText} />}
              />
            );
          })}
      </div>
    </PricingModal>
  );
};

export default PaymentOptionModal;
